import { useState } from 'react';
import { AppState } from 'core/redux/rootReducer';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { sendMessagingChannelMessage, sendAssistantConversationMessage } from 'messaging/gql/messagigMutations';
import { AssistantConversationQuery } from 'messaging/gql/useQueryChannels';
import { gqlUri } from 'core/config/gql/client';
import TokenService from 'core/config/gql/TokenService';
import SessionService from 'core/config/gql/SessionService';
import FirebaseTokenService from 'core/config/gql/FirebaseTokenService';
import { firebaseActions } from '../../firebase/redux/firebaseActions';
import { assistantSettingsProviderSelector } from '../../environment/redux/environmentReducer';
import { MESSAGING } from 'core/datasets/action';

export type SendChannelMessageResult = {
    messageId: string | null;
    shouldWaitForAiReply: boolean;
};

async function resolveAssistantConversationId(
    channelId: string | null,
    token: string,
    getState: () => AppState,
    dispatch: ReturnType<typeof useDispatch>,
): Promise<number | null> {
    const existingId = getState().messaging.assistantConversationId;
    if (existingId) {
        return existingId;
    }

    if (!channelId) {
        return null;
    }

    const queryBody = AssistantConversationQuery.loc?.source.body;

    try {
        const response = await axios({
            method: 'post',
            baseURL: gqlUri,
            headers: {
                'X-Auth-Token': token,
                'Content-Type': 'application/json',
            },
            data: {
                query: queryBody,
                variables: { messagingChannelId: channelId },
            },
        });

        const conversation = response.data?.data?.assistantConversation;
        if (!conversation?.id) {
            return null;
        }

        dispatch({
            type: MESSAGING.FETCHED_ASSISTANT_CONVERSATION,
            payload: { data: { assistantConversation: conversation } },
        });

        return conversation.id;
    } catch (error) {
        console.error('[AI] Failed to resolve assistant conversation', error);
        return null;
    }
}

const useAxiosSendMessage = () => {
    const dispatch = useDispatch();
    const store = useStore<AppState>();
    const tokenInstance = TokenService.getInstance();
    const sessionService = SessionService.getInstance();
    const firebaseTokenService = FirebaseTokenService.getInstance(
        useSelector(({ firebase }: AppState) => firebase.token) || '',
        useSelector(({ firebase }: AppState) => firebase.expireAt),
    );

    const newMessage = useSelector(({ messaging }: AppState) => messaging.newMessage);
    const channelId = useSelector(({ messaging }: AppState) => messaging.currentChannelId);
    const assistantProviderName = useSelector(assistantSettingsProviderSelector);

    const queryBody = sendMessagingChannelMessage.loc?.source.body;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [axiosData, setAxiosData] = useState<any>(null);
    const [axiosError, setAxiosError] = useState<AxiosError>();
    const [axiosLoading, setAxiosLoading] = useState(false);

    const createRequest = async (
        { files }: { files: File[] },
        sendToAI = false,
        message?: string | null,
        explicitChannelId?: string | null,
    ): Promise<SendChannelMessageResult> => {
        const resolvedChannelId = explicitChannelId ?? channelId;

        if (!resolvedChannelId) {
            console.error('[Messaging] Cannot send message without channel id');
            return { messageId: null, shouldWaitForAiReply: false };
        }

        if (tokenInstance.shouldRefreshToken()) {
            await tokenInstance.refreshToken().catch((error) => {
                console.error(`[Token refresh error]: ${error}`);
                sessionService.logoff();
            });
        }

        if (firebaseTokenService.shouldRefreshToken()) {
            await firebaseTokenService.refreshToken().catch((error) => {
                console.error(`[Firebase token refresh error]: ${error}`);
                sessionService.logoff();
            });

            dispatch(
                firebaseActions.refreshedToken(firebaseTokenService.getToken(), firebaseTokenService.getExpireAt()),
            );
        }

        const token = tokenInstance.getToken();
        const firebaseToken = firebaseTokenService.getToken();
        const formData = new FormData();
        setAxiosLoading(true);
        setAxiosError(() => undefined);

        const filesMap: { [key: string]: [string] } = {};
        const filesKeys = Object.keys(files);
        const dummyFilesArray: null[] = [];
        filesKeys.forEach((key) => {
            dummyFilesArray.push(null);
            filesMap[key] = ['variables.data.files.' + key];
            formData.append(key, files[Number(key)]);
        });
        const operations = {
            query: queryBody,
            variables: {
                data: {
                    messagingAccessToken: firebaseToken,
                    messagingChannelId: resolvedChannelId,
                    messageBody: message ?? newMessage,
                    files: dummyFilesArray,
                },
            },
        };
        formData.append('operations', JSON.stringify(operations));
        formData.append('map', JSON.stringify(filesMap));

        const config: AxiosRequestConfig = {
            headers: {
                'X-Auth-Token': `${token}`,
                Accept: 'application/json',
                'Content-Type': 'multipart/form-data',
            },
            method: 'post',
            baseURL: gqlUri,
            data: formData,
        };

        let messageId: string | null = null;

        try {
            const response = await axios(config);
            if ('errors' in response.data) setAxiosError(response.data.errors[0]);
            setAxiosData(() => response.data.data);
            messageId = response.data.data?.sendMessagingChannelMessage?.id ?? null;
        } catch (error) {
            console.error('error', error);
            setAxiosError(error as AxiosError);
        } finally {
            setAxiosLoading(false);
        }

        if (!sendToAI || !messageId) {
            return { messageId, shouldWaitForAiReply: false };
        }

        if (!assistantProviderName) {
            console.error('[AI] Assistant provider is not configured');
            return { messageId, shouldWaitForAiReply: false };
        }

        const assistantConversationId = await resolveAssistantConversationId(
            resolvedChannelId,
            token,
            store.getState,
            dispatch,
        );

        if (!assistantConversationId) {
            console.error('[AI] Assistant conversation is not ready');
            return { messageId, shouldWaitForAiReply: false };
        }

        const queryAIBody = sendAssistantConversationMessage.loc?.source.body;

        const operationsAI = {
            query: queryAIBody,
            variables: {
                data: {
                    assistantProvider: assistantProviderName,
                    assistantConversationId,
                    messagingMessageId: messageId,
                },
            },
        };

        const configAI: AxiosRequestConfig = {
            headers: {
                'X-Auth-Token': `${token}`,
                'Content-Type': 'application/json',
            },
            method: 'post',
            baseURL: gqlUri,
            data: operationsAI,
        };

        try {
            setAxiosLoading(true);
            const response = await axios(configAI);
            if ('errors' in response.data) {
                setAxiosError(response.data.errors[0]);
                return { messageId, shouldWaitForAiReply: false };
            }

            setAxiosData(() => response.data.data);
            return { messageId, shouldWaitForAiReply: true };
        } catch (error) {
            console.error('error', error);
            setAxiosError(error as AxiosError);
            return { messageId, shouldWaitForAiReply: false };
        } finally {
            setAxiosLoading(false);
        }
    };

    return { axiosError, axiosLoading, axiosData, createRequest };
};

export default useAxiosSendMessage;
